// Generated from @cascade/api-contracts. Do not edit.
// Run: pnpm --filter @cascade/api-client build

import type {
  TApiRequestOptions,
  TApiTransport,
} from '#request/request.types.ts';
import type {
  TBotAccessRequestPayload,
  TBotAccessResponsePayload,
  TBotFeedbackRequestPayload,
  TBotFeedbackResponsePayload,
  TBotOutboxAckRequestPayload,
  TBotOutboxAckResponsePayload,
  TBotOutboxPullRequestPayload,
  TBotOutboxPullResponsePayload,
  TBotSetTagsRequestPayload,
  TBotSetTagsResponsePayload,
  TBotSuggestedQuestionsRequestPayload,
  TBotSuggestedQuestionsResponsePayload,
  TBotTicketCreateRequestPayload,
  TBotTicketCreateResponsePayload,
  TBotTicketListRequestPayload,
  TBotTicketListResponsePayload,
  TBotTicketLookupRequestPayload,
  TBotTicketLookupResponsePayload,
  TBotTicketMessagesRequestPayload,
  TBotTicketMessagesResponsePayload,
  TBotTicketOptionsRequestPayload,
  TBotTicketOptionsResponsePayload,
  TBotTicketSetReadRequestPayload,
  TBotTicketSetReadResponsePayload,
  TBotTicketUpdateRequestPayload,
  TBotTicketUpdateResponsePayload,
  TBotTosAcceptRequestPayload,
  TBotTosAcceptResponsePayload,
  TBotTosTextRequestPayload,
  TBotTosTextResponsePayload,
  TBotUploadConversationFileRequest,
  TBotUploadConversationFileResponsePayload,
} from './bot.types.ts';

export const createBotApi = (transport: TApiTransport) => ({
  /** Checks bot chat access and terms state for a user */
  botAccess: (
    payload: TBotAccessRequestPayload,
    options?: TApiRequestOptions,
  ) =>
    transport.post<TBotAccessRequestPayload, TBotAccessResponsePayload>(
      '/bot_access',
      payload,
      options,
    ),

  /** Records feedback on a bot answer */
  botFeedback: (
    payload: TBotFeedbackRequestPayload,
    options?: TApiRequestOptions,
  ) =>
    transport.post<TBotFeedbackRequestPayload, TBotFeedbackResponsePayload>(
      '/bot_feedback',
      payload,
      options,
    ),

  /** Acknowledges delivery outcome for claimed bot outbox events */
  botOutboxAck: (
    payload: TBotOutboxAckRequestPayload,
    options?: TApiRequestOptions,
  ) =>
    transport.post<TBotOutboxAckRequestPayload, TBotOutboxAckResponsePayload>(
      '/bot_outbox_ack',
      payload,
      options,
    ),

  /** Claims pending ticket events for one bot channel in the projects the caller names. Run one consumer app per backend and channel. Several apps on one channel need consumer routing before polling. */
  botOutboxPull: (
    payload: TBotOutboxPullRequestPayload,
    options?: TApiRequestOptions,
  ) =>
    transport.post<TBotOutboxPullRequestPayload, TBotOutboxPullResponsePayload>(
      '/bot_outbox_pull',
      payload,
      options,
    ),

  /** Sets a bot user's tags */
  botSetTags: (
    payload: TBotSetTagsRequestPayload,
    options?: TApiRequestOptions,
  ) =>
    transport.post<TBotSetTagsRequestPayload, TBotSetTagsResponsePayload>(
      '/bot_set_tags',
      payload,
      options,
    ),

  /** Returns a bot user's suggested questions, region-aware via their tags */
  botSuggestedQuestions: (
    payload: TBotSuggestedQuestionsRequestPayload,
    options?: TApiRequestOptions,
  ) =>
    transport.post<
      TBotSuggestedQuestionsRequestPayload,
      TBotSuggestedQuestionsResponsePayload
    >('/bot_suggested_questions', payload, options),

  /** Creates a support ticket from a bot conversation */
  botTicketCreate: (
    payload: TBotTicketCreateRequestPayload,
    options?: TApiRequestOptions,
  ) =>
    transport.post<
      TBotTicketCreateRequestPayload,
      TBotTicketCreateResponsePayload
    >('/bot_ticket_create', payload, options),

  /** Lists a bot user's own tickets in a project */
  botTicketList: (
    payload: TBotTicketListRequestPayload,
    options?: TApiRequestOptions,
  ) =>
    transport.post<TBotTicketListRequestPayload, TBotTicketListResponsePayload>(
      '/bot_ticket_list',
      payload,
      options,
    ),

  /** Looks up a bot user's support tickets */
  botTicketLookup: (
    payload: TBotTicketLookupRequestPayload,
    options?: TApiRequestOptions,
  ) =>
    transport.post<
      TBotTicketLookupRequestPayload,
      TBotTicketLookupResponsePayload
    >('/bot_ticket_lookup', payload, options),

  /** Returns one of a bot user's own tickets with its reporter-visible thread */
  botTicketMessages: (
    payload: TBotTicketMessagesRequestPayload,
    options?: TApiRequestOptions,
  ) =>
    transport.post<
      TBotTicketMessagesRequestPayload,
      TBotTicketMessagesResponsePayload
    >('/bot_ticket_messages', payload, options),

  /** Lists the ticket destinations and escalation form fields a bot user can file into */
  botTicketOptions: (
    payload: TBotTicketOptionsRequestPayload,
    options?: TApiRequestOptions,
  ) =>
    transport.post<
      TBotTicketOptionsRequestPayload,
      TBotTicketOptionsResponsePayload
    >('/bot_ticket_options', payload, options),

  /** Marks the other side's messages on a bot user's own ticket as read */
  botTicketSetRead: (
    payload: TBotTicketSetReadRequestPayload,
    options?: TApiRequestOptions,
  ) =>
    transport.post<
      TBotTicketSetReadRequestPayload,
      TBotTicketSetReadResponsePayload
    >('/bot_ticket_set_read', payload, options),

  /** Posts a bot user's reply and/or close/reopen on their own ticket */
  botTicketUpdate: (
    payload: TBotTicketUpdateRequestPayload,
    options?: TApiRequestOptions,
  ) =>
    transport.post<
      TBotTicketUpdateRequestPayload,
      TBotTicketUpdateResponsePayload
    >('/bot_ticket_update', payload, options),

  /** Records terms acceptance from a bot user */
  botTosAccept: (
    payload: TBotTosAcceptRequestPayload,
    options?: TApiRequestOptions,
  ) =>
    transport.post<TBotTosAcceptRequestPayload, TBotTosAcceptResponsePayload>(
      '/bot_tos_accept',
      payload,
      options,
    ),

  /** Returns the global terms text shown to bot users */
  botTosText: (
    payload: TBotTosTextRequestPayload,
    options?: TApiRequestOptions,
  ) =>
    transport.post<TBotTosTextRequestPayload, TBotTosTextResponsePayload>(
      '/bot_tos_text',
      payload,
      options,
    ),

  /** Uploads a file to a bot conversation */
  botUploadConversationFile: (
    payload: TBotUploadConversationFileRequest,
    options?: TApiRequestOptions,
  ) =>
    transport.post<
      TBotUploadConversationFileRequest,
      TBotUploadConversationFileResponsePayload
    >('/bot_upload_conversation_file', payload, {
      ...options,
      contentType: 'multipart/form-data',
    }),
});
